import React, { useState, useContext, useMemo } from "react";
import { Button, Input, Modal, Select, Space, Tag } from "antd";
import { AuthContext } from "../../context/AuthContext";
import { useGlobalToast } from "../../context/ToastProvider";
import { useTranslation } from "react-i18next";
import { isColorDark } from "../sideBar/sideBar";
import { NotificationOutlined } from "@ant-design/icons";
import { sendFleetBroadcastMessage } from "../../services/fleetConversationService";
import "./Broadcast.css";

const Chat = (props) => {
  const { compact = false, showTags = true, tags = [], drivers = [] } = props;
  const [isExpanded, setIsExpanded] = useState(!compact);
  const [broadcastMessage, setBroadcastMessage] = useState("");
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [isRecipientsDropdownOpen, setIsRecipientsDropdownOpen] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const { getFleetID } = useContext(AuthContext);
  const toast = useGlobalToast();
  const { t } = useTranslation();
  const primaryColor = "var(--chakra-colors-gandolapp-500)";
  const buttonTextColor = isColorDark(primaryColor) ? "white" : "black";

  const recipientsSelection = useMemo(() => {
    const selectedTagSet = new Set();
    const selectedDriverIdSet = new Set();

    selectedRecipients.forEach((value) => {
      const text = String(value || "");
      if (text.startsWith("tag:")) {
        const tag = text.slice("tag:".length);
        if (tag) selectedTagSet.add(tag);
        return;
      }
      if (text.startsWith("driver:")) {
        const driverId = text.slice("driver:".length);
        if (driverId) selectedDriverIdSet.add(driverId);
      }
    });

    return {
      tags: Array.from(selectedTagSet),
      driverIds: Array.from(selectedDriverIdSet),
    };
  }, [selectedRecipients]);

  const sendBroadcastMessage = async () => {
    if (!broadcastMessage.trim().length || isSending) return;
    setIsSending(true);
    try {
      const fleetId = getFleetID();
      if (!fleetId) return;

      const hasTagFilter = recipientsSelection.tags.length > 0;
      const hasDriverFilter = recipientsSelection.driverIds.length > 0;
      const selectedDriverSet = new Set(recipientsSelection.driverIds.map((driverId) => String(driverId)));

      const visibleDriverIds = drivers
        .filter((driverDoc) => {
          if (!hasTagFilter && !hasDriverFilter) return true;
          const driverTags = Array.isArray(driverDoc.tags) ? driverDoc.tags : [];
          const matchesTag =
            hasTagFilter &&
            driverTags.some((driverTag) => recipientsSelection.tags.includes(String(driverTag)));
          const matchesDriver = hasDriverFilter && selectedDriverSet.has(String(driverDoc.id || ""));
          return matchesTag || matchesDriver;
        })
        .map((driverDoc) => String(driverDoc.id || "").trim())
        .filter(Boolean);
      const uniqueVisibleDriverIds = [...new Set(visibleDriverIds)];

      if (drivers.length > 0 && uniqueVisibleDriverIds.length === 0) {
        toast({
          title: t("driverFilter.noDriversFound"),
          status: "warning",
        });
        return;
      }

      const response = await sendFleetBroadcastMessage({
        fleetId,
        message: broadcastMessage,
        tags: recipientsSelection.tags,
        driverIds: recipientsSelection.driverIds,
      });

      toast({
        title: t("toast.successBroadcastMessage", {
          count: response?.deliveredCount || response?.messagesSent || undefined,
        }),
        status: "success",
      });
      setBroadcastMessage("");
      setSelectedRecipients([]);
      if (compact) {
        setIsExpanded(false);
      }
    } catch (error) {
      toast({ error,
        title: t("toast.errorBroadcastMessage"),
        status: "warning",
      });
      console.log(error);
    } finally {
      setIsSending(false);
    }
  };

  const handleBroadcastMessage = async () => {
    if (!broadcastMessage.trim().length) return;
    setIsConfirmModalOpen(true);
  };

  const uniqueTags = [...new Set(tags)];
  const uniqueDrivers = useMemo(() => {
    const map = new Map();
    drivers.forEach((driver) => {
      const driverId = String(driver?.id || "").trim();
      if (!driverId) return;
      if (!map.has(driverId)) {
        map.set(driverId, {
          value: driverId,
          label: String(driver?.name || driverId).trim() || driverId,
        });
      }
    });
    return Array.from(map.values());
  }, [drivers]);
  const driverLabelById = useMemo(() => {
    const map = new Map();
    uniqueDrivers.forEach((driver) => {
      map.set(String(driver.value), String(driver.label));
    });
    return map;
  }, [uniqueDrivers]);
  const hasExplicitRecipients =
    recipientsSelection.tags.length > 0 || recipientsSelection.driverIds.length > 0;
  const emptyValue = t("contextToolbar.broadcastConfirmNone", { defaultValue: "None" });
  const removeRecipientValue = (recipientValue) => {
    setSelectedRecipients((previousRecipients) =>
      previousRecipients.filter((currentRecipient) => currentRecipient !== recipientValue)
    );
  };
  const confirmSendBroadcast = async () => {
    setIsConfirmModalOpen(false);
    await sendBroadcastMessage();
  };

  const recipientOptions = useMemo(() => {
    const options = [];

    if (showTags) {
      options.push({
        label: t("contextToolbar.broadcastRecipientsGroupTags", { defaultValue: "Tags" }),
        options: uniqueTags.map((tag) => ({
          label: tag,
          value: `tag:${tag}`,
        })),
      });
    }

    options.push({
      label: t("contextToolbar.broadcastRecipientsGroupDrivers", { defaultValue: "Drivers" }),
      options: uniqueDrivers.map((driver) => ({
        label: driver.label,
        value: `driver:${driver.value}`,
      })),
    });

    return options;
  }, [showTags, t, uniqueDrivers, uniqueTags]);
  const trySubmitBroadcast = (event) => {
    event?.preventDefault?.();
    if (isRecipientsDropdownOpen) return;
    void handleBroadcastMessage();
  };
  const handleRecipientsInputKeyDown = (event) => {
    if (event.key !== "Enter") return;
    if (isRecipientsDropdownOpen) return;
    event.preventDefault();
    void handleBroadcastMessage();
  };
  const recipientsSummaryContent = (
    <Space direction="vertical" size={4}>
      <div>
        <strong>{t("contextToolbar.broadcastConfirmMessageLabel", { defaultValue: "Message" })}:</strong>{" "}
        {broadcastMessage.trim()}
      </div>
      <div>
        <strong>
          {t("contextToolbar.broadcastConfirmRecipientsLabel", {
            defaultValue: "Recipients",
          })}
          :
        </strong>
      </div>
      <div>
        <strong>{t("contextToolbar.broadcastConfirmTagsLabel", { defaultValue: "Tags" })}:</strong>{" "}
        {recipientsSelection.tags.length ? (
          <Space size={4} wrap>
            {recipientsSelection.tags.map((tag) => (
              <Tag
                key={tag}
                color="gold"
                closable
                onClose={(event) => {
                  event.preventDefault();
                  removeRecipientValue(`tag:${tag}`);
                }}
              >
                {tag}
              </Tag>
            ))}
          </Space>
        ) : (
          emptyValue
        )}
      </div>
      <div>
        <strong>{t("contextToolbar.broadcastConfirmDriversLabel", { defaultValue: "Drivers" })}:</strong>{" "}
        {recipientsSelection.driverIds.length ? (
          <Space size={4} wrap>
            {recipientsSelection.driverIds.map((driverId) => {
              const driverName = driverLabelById.get(String(driverId)) || String(driverId);
              return (
                <Tag
                  key={driverId}
                  color="blue"
                  closable
                  onClose={(event) => {
                    event.preventDefault();
                    removeRecipientValue(`driver:${driverId}`);
                  }}
                >
                  {driverName}
                </Tag>
              );
            })}
          </Space>
        ) : (
          emptyValue
        )}
      </div>
      {!hasExplicitRecipients ? (
        <div>{t("contextToolbar.broadcastConfirmAllDrivers", { defaultValue: "All drivers in this fleet" })}</div>
      ) : null}
      <div>{t("contextToolbar.broadcastConfirmQuestion", { defaultValue: "Send this broadcast?" })}</div>
    </Space>
  );
  const confirmModal = (
    <Modal
      open={isConfirmModalOpen}
      title={t("driverPage.broadcast")}
      okText={t("driverPage.sendButton")}
      cancelText={t("setup.cancel", { defaultValue: "Cancel" })}
      okButtonProps={{ autoFocus: true }}
      confirmLoading={isSending}
      onOk={confirmSendBroadcast}
      onCancel={() => setIsConfirmModalOpen(false)}
      destroyOnClose
    >
      {recipientsSummaryContent}
    </Modal>
  );

  if (compact) {
    return (
      <>
        <div className={`broadcast-collapse-shell ${isExpanded ? "expanded" : ""}`}>
          <Button
            className="broadcast-toggle-btn"
            type={isExpanded ? "default" : "primary"}
            shape="circle"
            icon={<NotificationOutlined />}
            onClick={() => setIsExpanded((prev) => !prev)}
            style={isExpanded ? {} : { background: primaryColor, color: buttonTextColor }}
            title={t("contextToolbar.broadcastLabel", {
              defaultValue: t("driverPage.broadcast"),
            })}
          >
            {null}
          </Button>
          <div className="broadcast-collapse-panel">
            <form onSubmit={trySubmitBroadcast}>
              <Space align="center" className="broadcast-inline">
                <Select
                  mode="multiple"
                  showSearch
                  optionFilterProp="label"
                  placeholder={t("contextToolbar.broadcastRecipientsPlaceholder", {
                    defaultValue: "Tags & drivers",
                  })}
                  options={recipientOptions}
                  notFoundContent={t("contextToolbar.noRecipientsAvailable", {
                    defaultValue: "No recipients available",
                  })}
                  value={selectedRecipients}
                  onChange={(values) => setSelectedRecipients(values)}
                  onDropdownVisibleChange={setIsRecipientsDropdownOpen}
                  onInputKeyDown={handleRecipientsInputKeyDown}
                  style={{ width: 260 }}
                  size="small"
                  maxTagCount="responsive"
                />
                <Input
                  value={broadcastMessage}
                  onChange={(e) => setBroadcastMessage(e.target.value)}
                  onPressEnter={trySubmitBroadcast}
                  placeholder={t("driverPage.broadcast")}
                  style={{ width: 240 }}
                  size="small"
                />
                <Button
                  htmlType="submit"
                  style={{ background: primaryColor, color: buttonTextColor }}
                  size="small"
                  loading={isSending}
                >
                  {t("driverPage.sendButton")}
                </Button>
              </Space>
            </form>
          </div>
        </div>
        {confirmModal}
      </>
    );
  }

  return (
    <>
      <form onSubmit={trySubmitBroadcast}>
        <Space align="center" wrap>
          <Select
            mode="multiple"
            showSearch
            optionFilterProp="label"
            placeholder={t("contextToolbar.broadcastRecipientsPlaceholder", {
              defaultValue: "Tags & drivers",
            })}
            options={recipientOptions}
            notFoundContent={t("contextToolbar.noRecipientsAvailable", {
              defaultValue: "No recipients available",
            })}
            value={selectedRecipients}
            onChange={(values) => setSelectedRecipients(values)}
            onDropdownVisibleChange={setIsRecipientsDropdownOpen}
            onInputKeyDown={handleRecipientsInputKeyDown}
            style={{ minWidth: 220 }}
          />
          <Input
            value={broadcastMessage}
            onChange={(e) => setBroadcastMessage(e.target.value)}
            onPressEnter={trySubmitBroadcast}
            placeholder={t("driverPage.broadcast")}
            style={{ width: 300 }}
          />
          <Button
            htmlType="submit"
            style={{ background: primaryColor, color: buttonTextColor }}
            loading={isSending}
          >
            {t("driverPage.sendButton")}
          </Button>
        </Space>
      </form>
      {confirmModal}
    </>
  );
};
export default Chat;
